import { mkdir, mkdtemp, readFile, rm, unlink, writeFile } from "node:fs/promises";
import { tmpdir } from "node:os";
import { dirname, join } from "node:path";
import { minimatch } from "minimatch";
import { MergeAccumulator, type AccumulatorEntry } from "./accumulator/MergeAccumulator.js";
import { buildMergePlan, resolveInputs, runMergeAndRecord } from "./applicator/index.js";
import { stripConflictMarkers } from "./conflict-utils.js";
import type { GitClient } from "./git/GitClient.js";
import type { LockfileManager } from "./LockfileManager.js";
import { isBinaryFile } from "./shared/binary.js";
import type {
    ConflictReason,
    FileResult,
    GenerationRecord,
    ReplayResult,
    StoredPatch
} from "./types.js";

// Re-exported from `./shared/binary.js` — kept here for back-compat with
// external imports (`@fern-api/replay/dist/ReplayApplicator`). New code
// should import from `./shared/binary.js` directly.
export { isBinaryFile } from "./shared/binary.js";

export class ReplayApplicator {
    private git: GitClient;
    private lockManager: LockfileManager;
    private outputDir: string;
    private renameCache = new Map<string, Array<{ from: string; to: string }>>();
    private treeExistsCache = new Map<string, boolean>();

    /**
     * Inter-patch THEIRS coordination. Created fresh in each `applyPatches`
     * invocation with the run's apply mode + precomputed shared-file set.
     * See `src/accumulator/MergeAccumulator.ts` for the full contract.
     *
     * Initialized to an empty default in the constructor so type narrowing
     * works for callers that read it before any `applyPatches` invocation
     * (e.g., the `getAccumulatorSnapshot` test accessor).
     */
    private accumulator = new MergeAccumulator("replay", new Set());

    constructor(git: GitClient, lockManager: LockfileManager, outputDir: string) {
        this.git = git;
        this.lockManager = lockManager;
        this.outputDir = outputDir;
    }

    /**
     * Resolve the GenerationRecord for a patch's base_generation.
     * Falls back to constructing an ad-hoc record from the commit's tree
     * when base_generation isn't a tracked generation (commit-scan patches).
     */
    private async resolveBaseGeneration(baseGeneration: string): Promise<GenerationRecord | undefined> {
        const gen = this.lockManager.getGeneration(baseGeneration);
        if (gen) return gen;
        try {
            const treeHash = await this.git.getTreeHash(baseGeneration);
            return {
                commit_sha: baseGeneration,
                tree_hash: treeHash,
                timestamp: new Date().toISOString(),
                cli_version: "unknown",
                generator_versions: {}
            };
        } catch {
            return undefined;
        }
    }

    /**
     * @internal Test-only.
     * Returns a defensive copy of the inter-patch accumulator. Used by
     * invariant I2 to assert that after every applied patch, the
     * accumulator has an entry for each file the patch touched, and the
     * entry's content matches on-disk.
     */
    getAccumulatorSnapshot(): ReadonlyMap<string, AccumulatorEntry> {
        return this.accumulator.snapshot();
    }

    /**
     * Apply all patches, returning results for each.
     * Skips patches that match exclude patterns in replay.yml
     *
     * `applyMode` controls the post-conflict marker strategy:
     *   - `"replay"` (default): strip conflict markers from disk between
     *     iterations whenever a later patch touches the same file. Lets the
     *     follow-up patch's `git apply --3way` see clean OURS content,
     *     which is what the silent-loss fix (PR #73) relies on. The replay
     *     pipeline calls `revertConflictingFiles` after the loop to clean
     *     any markers that survive.
     *   - `"resolve"`: keep markers on disk. The resolve command needs the
     *     customer to see them. Slow-path 3-way merge naturally preserves
     *     A's markers and B's clean writes when their regions don't overlap;
     *     when they do overlap, the customer gets nested markers and
     *     resolves manually. Either way they aren't silently dropped.
     */
    async applyPatches(
        patches: StoredPatch[],
        opts?: { applyMode?: "replay" | "resolve" }
    ): Promise<ReplayResult[]> {
        const applyMode = opts?.applyMode ?? "replay";

        // Compute set of files shared by 2+ patches in this run. Used by
        // the accumulator to gate snapshot-as-primary in mergeFile and
        // populateAccumulatorForPatch — see cross-patch isolation.
        const fileFreq = new Map<string, number>();
        for (const p of patches) {
            for (const f of p.files) {
                fileFreq.set(f, (fileFreq.get(f) ?? 0) + 1);
            }
        }
        const sharedFiles = new Set(
            Array.from(fileFreq.entries())
                .filter(([, n]) => n > 1)
                .map(([f]) => f),
        );

        // Fresh accumulator per apply cycle — single-use, single owner.
        this.accumulator = new MergeAccumulator(applyMode, sharedFiles);

        const results: ReplayResult[] = [];

        for (let i = 0; i < patches.length; i++) {
            const patch = patches[i]!;

            if (this.isExcluded(patch)) {
                results.push({
                    patch,
                    status: "skipped",
                    method: "git-am"
                });
                continue;
            }

            const result = await this.applyPatchWithFallback(patch);
            results.push(result);

            // Strip conflict markers from files that a later patch will also
            // touch. This prevents marker cascade into the next patch's OURS
            // while preserving markers on files only this patch touches (needed
            // by the resolve workflow for user resolution). Only fires in
            // replay mode — resolve mode keeps markers on disk so the customer
            // can see and edit them.
            if (applyMode !== "resolve" && result.status === "conflict" && result.fileResults) {
                // Later patches list original (pre-rename) paths in their files array.
                const laterFiles = new Set<string>();
                for (let j = i + 1; j < patches.length; j++) {
                    for (const f of patches[j]!.files) {
                        laterFiles.add(f);
                    }
                }

                // Build reverse rename map: resolved path → original path.
                // fileResult.file is the resolved path, but laterFiles has originals.
                const resolvedToOriginal = new Map<string, string>();
                if (result.resolvedFiles) {
                    for (const [orig, resolved] of Object.entries(result.resolvedFiles)) {
                        resolvedToOriginal.set(resolved, orig);
                    }
                }

                for (const fileResult of result.fileResults) {
                    if (fileResult.status !== "conflict") continue;
                    // Check both resolved and original paths against later patches
                    const originalPath = resolvedToOriginal.get(fileResult.file) ?? fileResult.file;
                    if (laterFiles.has(fileResult.file) || laterFiles.has(originalPath)) {
                        const filePath = join(this.outputDir, fileResult.file);
                        try {
                            const content = await readFile(filePath, "utf-8");
                            const stripped = stripConflictMarkers(content);
                            await writeFile(filePath, stripped);
                        } catch {
                            // File doesn't exist or can't be read — skip
                        }
                    }
                }
            }
        }

        return results;
    }

    /**
     * Populate accumulator after git apply succeeds, AND collect per-file
     * results including any "dropped context lines" — lines that were
     * present in BASE and THEIRS (unchanged context) but absent from the
     * final on-disk state because OURS deleted them. This is the fast-path
     * counterpart to ThreeWayMerge.computeDroppedContextLines used by the
     * 3-way slow path; both must surface the same warning.
     */
    private async populateAccumulatorForPatch(
        patch: StoredPatch,
        baseGen: GenerationRecord | undefined,
        currentTreeHash: string
    ): Promise<FileResult[]> {
        const fileResults: FileResult[] = [];
        if (!baseGen) return fileResults;

        // Create temp git repo to apply patches to base content
        const tempDir = await mkdtemp(join(tmpdir(), "replay-acc-"));
        const { GitClient } = await import("./git/GitClient.js");
        const tempGit = new GitClient(tempDir);
        await tempGit.exec(["init"]);
        await tempGit.exec(["config", "user.email", "replay@fern.com"]);
        await tempGit.exec(["config", "user.name", "Fern Replay"]);

        try {
            for (const filePath of patch.files) {
                if (isBinaryFile(filePath)) continue;

                const resolvedPath = await this.resolveFilePath(filePath, baseGen.tree_hash, currentTreeHash);

                const base = await this.git.showFile(baseGen.tree_hash, filePath);

                // For non-shared-file patches, the stored snapshot IS this
                // patch's THEIRS for the accumulator (individual contribution
                // by definition: only this patch touches the file). Skip
                // reconstruction. For shared-file patches, fall back to
                // line-anchored reconstruction so the accumulator entry is
                // each patch's INDIVIDUAL contribution (cross-patch isolation).
                // See `mergeFile` for the full rationale.
                const snapshotForFile =
                    patch.theirs_snapshot?.[resolvedPath] ?? patch.theirs_snapshot?.[filePath];
                const fileIsShared =
                    this.accumulator.isShared(resolvedPath) || this.accumulator.isShared(filePath);
                const theirs = !fileIsShared && snapshotForFile != null
                    ? snapshotForFile
                    : await this.applyPatchToContent(base, patch.patch_content, filePath, tempGit, tempDir);

                // For user-created files (base=null), applyPatchToContent may return null
                // for modification diffs. Fall back to reading the on-disk content that
                // git apply --3way just wrote successfully.
                const finalOnDisk = await readFile(join(this.outputDir, resolvedPath), "utf-8").catch(() => null);
                const effectiveTheirs = theirs ?? finalOnDisk;
                if (effectiveTheirs != null) {
                    this.accumulator.recordMerge(resolvedPath, effectiveTheirs, patch.base_generation);
                }

                // Detect lines that were in BOTH base and theirs but absent
                // from the final on-disk state — silently dropped by OURS.
                if (base != null && effectiveTheirs != null && finalOnDisk != null) {
                    const dropped = computeDroppedContextLinesForFile(base, effectiveTheirs, finalOnDisk);
                    if (dropped.length > 0) {
                        fileResults.push({
                            file: resolvedPath,
                            status: "merged",
                            droppedContextLines: dropped
                        });
                    }
                }
            }
        } finally {
            await rm(tempDir, { recursive: true }).catch(() => {});
        }
        return fileResults;
    }

    private async applyPatchWithFallback(patch: StoredPatch): Promise<ReplayResult> {
        // Resolve all file paths to check accumulator (need baseGen for resolution)
        const baseGen = await this.resolveBaseGeneration(patch.base_generation);
        const lock = this.lockManager.read();
        const currentGen = lock.generations.find((g) => g.commit_sha === lock.current_generation);
        const currentTreeHash = currentGen?.tree_hash ?? baseGen?.tree_hash ?? "";

        // Check if any file in this patch needs accumulation
        // If so, skip fast path to ensure we benefit from pre-merge logic
        const needsAccumulation = await Promise.all(
            patch.files.map(async (f) => {
                if (!baseGen) return false;
                const resolved = await this.resolveFilePath(f, baseGen.tree_hash, currentTreeHash);
                return this.accumulator.has(resolved);
            })
        ).then((results) => results.some(Boolean));

        // Pre-compute resolved paths. If the tree-level rename/copy detection says
        // a file moved but the patch content itself doesn't describe the rename,
        // this is a GENERATOR rename and the git-apply fast path is unsafe: it would
        // apply the content diff against the original path (even if still on disk)
        // rather than the resolved new path. The 3-way merge path uses `resolvedPath`
        // for OURS and for the output write, so it handles this correctly.
        //
        // USER renames (patch contains `rename from`/`rename to` headers) go through
        // the fast path because git apply handles those natively — skipping the fast
        // path there would break user-rename scenarios.
        const resolvedFiles: Record<string, string> = {};
        for (const filePath of patch.files) {
            const resolvedPath = baseGen
                ? await this.resolveFilePath(filePath, baseGen.tree_hash, currentTreeHash)
                : filePath;
            if (resolvedPath !== filePath) {
                resolvedFiles[filePath] = resolvedPath;
            }
        }
        const patchIsRenameAware = /^rename from /m.test(patch.patch_content);
        const hasGeneratorRename = Object.keys(resolvedFiles).length > 0 && !patchIsRenameAware;

        // Strategy 1: Try git apply --3way (skip if accumulation needed or a generator rename was detected)
        if (!needsAccumulation && !hasGeneratorRename) {
            // Snapshot files before git apply (may leave conflict markers on failure)
            const snapshots = new Map<string, string | null>();
            for (const filePath of patch.files) {
                const fullPath = join(this.outputDir, filePath);
                snapshots.set(filePath, await readFile(fullPath, "utf-8").catch(() => null));
            }

            try {
                await this.git.execWithInput(["apply", "--3way"], patch.patch_content);

                // Success! Populate accumulator so subsequent patches on these files
                // will skip fast path and use the pre-merge logic instead.
                // Also collect per-file results (notably droppedContextLines).
                const fastPathFileResults = await this.populateAccumulatorForPatch(patch, baseGen, currentTreeHash);

                return {
                    patch,
                    status: "applied",
                    method: "git-am",
                    ...(fastPathFileResults.length > 0 && { fileResults: fastPathFileResults }),
                    ...(Object.keys(resolvedFiles).length > 0 && { resolvedFiles })
                };
            } catch {
                // git apply --3way failed and may have left conflict markers on disk
                // Restore files from snapshot to undo any corruption before fallback
                for (const [filePath, content] of snapshots) {
                    const fullPath = join(this.outputDir, filePath);
                    if (content != null) {
                        await writeFile(fullPath, content);
                    } else {
                        // Delete files created by the failed git apply that didn't exist before
                        await unlink(fullPath).catch(() => {});
                    }
                }
            }
        }

        // Strategy 2: Fall back to file-by-file 3-way merge (uses accumulator)
        return this.applyWithThreeWayMerge(patch);
    }

    private async applyWithThreeWayMerge(patch: StoredPatch): Promise<ReplayResult> {
        const fileResults: FileResult[] = [];
        const resolvedFiles: Record<string, string> = {};

        const tempDir = await mkdtemp(join(tmpdir(), "replay-"));
        const { GitClient } = await import("./git/GitClient.js");
        const tempGit = new GitClient(tempDir);
        await tempGit.exec(["init"]);
        await tempGit.exec(["config", "user.email", "replay@fern.com"]);
        await tempGit.exec(["config", "user.name", "Fern Replay"]);

        try {
            for (const filePath of patch.files) {
                if (isBinaryFile(filePath)) {
                    fileResults.push({
                        file: filePath,
                        status: "skipped",
                        reason: "binary-file"
                    });
                    continue;
                }
                const result = await this.mergeFile(patch, filePath, tempGit, tempDir);
                if (result.file !== filePath) {
                    resolvedFiles[filePath] = result.file;
                }
                fileResults.push(result);
            }
        } finally {
            await rm(tempDir, { recursive: true }).catch(() => {});
        }

        const conflictFiles = fileResults.filter((r) => r.status === "conflict");
        const hasConflicts = conflictFiles.length > 0;

        // Aggregate conflict reason from file-level to patch-level
        const conflictReason: ConflictReason | undefined = hasConflicts
            ? conflictFiles.some((f) => f.conflictReason === "base-generation-mismatch")
                ? "base-generation-mismatch"
                : conflictFiles[0]?.conflictReason
            : undefined;

        return {
            patch,
            status: hasConflicts ? "conflict" : "applied",
            method: "3way-merge",
            fileResults,
            conflictReason,
            ...(Object.keys(resolvedFiles).length > 0 && { resolvedFiles })
        };
    }

    private async mergeFile(
        patch: StoredPatch,
        filePath: string,
        tempGit: GitClient,
        tempDir: string
    ): Promise<FileResult> {
        try {
            const inputs = await resolveInputs({
                patch,
                filePath,
                git: this.git,
                lockManager: this.lockManager,
                outputDir: this.outputDir,
                isTreeReachable: (h) => this.isTreeReachable(h),
                resolveBaseGeneration: (s) => this.resolveBaseGeneration(s),
                resolveFilePath: (f, b, c) => this.resolveFilePath(f, b, c),
                extractRenameSource: (p, f) => this.extractRenameSource(p, f),
                extractFileDiff: (p, f) => this.extractFileDiff(p, f),
            });
            if (inputs.earlyExit) {
                return { file: filePath, ...inputs.earlyExit };
            }
            const plan = await buildMergePlan({
                inputs,
                patch,
                filePath,
                accumulator: this.accumulator,
                tempGit,
                tempDir,
                applyPatchToContent: (b, p, f, g, d, s) =>
                    this.applyPatchToContent(b, p, f, g, d, s),
                isPatchAlreadyApplied: (p, f, c, g, d) =>
                    this.isPatchAlreadyApplied(p, f, c, g, d),
            });
            return await runMergeAndRecord({
                plan,
                inputs,
                patch,
                accumulator: this.accumulator,
            });
        } catch (error) {
            return {
                file: filePath,
                status: "skipped",
                reason: `error: ${error instanceof Error ? error.message : String(error)}`
            };
        }
    }

    private async isTreeReachable(treeHash: string): Promise<boolean> {
        let result = this.treeExistsCache.get(treeHash);
        if (result === undefined) {
            result = await this.git.treeExists(treeHash);
            this.treeExistsCache.set(treeHash, result);
        }
        return result;
    }

    private isExcluded(patch: StoredPatch): boolean {
        const config = this.lockManager.getCustomizationsConfig();
        if (!config.exclude) return false;

        return patch.files.some((file) => config.exclude!.some((pattern) => minimatch(file, pattern)));
    }

    private async resolveFilePath(filePath: string, baseTreeHash: string, currentTreeHash: string): Promise<string> {
        // Priority 1: Manual moves from replay.yml
        const config = this.lockManager.getCustomizationsConfig();
        if (config.moves) {
            for (const move of config.moves) {
                if (minimatch(filePath, move.from) || filePath === move.from) {
                    // For exact matches, replace directly
                    if (filePath === move.from) {
                        return move.to;
                    }
                    // For glob matches, replace the matching prefix
                    // e.g., from: "src/api/**" to: "src/resources/**"
                    // filePath: "src/api/client.ts" → "src/resources/client.ts"
                    const fromBase = move.from.replace(/\*\*.*$/, "");
                    const toBase = move.to.replace(/\*\*.*$/, "");
                    if (filePath.startsWith(fromBase)) {
                        return toBase + filePath.slice(fromBase.length);
                    }
                }
            }
        }

        // Priority 2: Git rename detection (cached per tree pair)
        const cacheKey = `${baseTreeHash}:${currentTreeHash}`;
        let renames = this.renameCache.get(cacheKey);
        if (!renames) {
            renames = await this.git.detectRenames(baseTreeHash, currentTreeHash);
            this.renameCache.set(cacheKey, renames);
        }

        const gitRename = renames.find((r) => r.from === filePath);
        if (gitRename) {
            return gitRename.to;
        }

        // Priority 3: No rename detected
        return filePath;
    }

    private async applyPatchToContent(
        base: string | null,
        patchContent: string,
        filePath: string,
        tempGit: GitClient,
        tempDir: string,
        sourceFilePath?: string
    ): Promise<string | null> {
        if (!base) {
            // New file - extract content directly from patch
            return this.extractNewFileFromPatch(patchContent, filePath);
        }

        // Extract only the diff for this specific file
        const fileDiff = this.extractFileDiff(patchContent, filePath);
        if (!fileDiff) return null;

        try {
            if (sourceFilePath) {
                // Rename case: write base at the OLD path, apply the rename diff,
                // then read from the NEW path (filePath).
                const tempSourcePath = join(tempDir, sourceFilePath);
                await mkdir(dirname(tempSourcePath), { recursive: true });
                await writeFile(tempSourcePath, base);

                await tempGit.exec(["add", sourceFilePath]);
                await tempGit.exec([
                    "commit",
                    "-m",
                    `base for rename ${sourceFilePath} -> ${filePath}`,
                    "--allow-empty"
                ]);

                await tempGit.execWithInput(["apply", "--allow-empty"], fileDiff);

                const tempTargetPath = join(tempDir, filePath);
                return await readFile(tempTargetPath, "utf-8");
            }

            // Normal case: write base at filePath, apply diff, read back
            const tempFilePath = join(tempDir, filePath);
            await mkdir(dirname(tempFilePath), { recursive: true });
            await writeFile(tempFilePath, base);

            // Stage and commit so git apply has a clean base
            await tempGit.exec(["add", filePath]);
            await tempGit.exec(["commit", "-m", `base for ${filePath}`, "--allow-empty"]);

            // Apply this file's diff
            await tempGit.execWithInput(["apply", "--allow-empty"], fileDiff);

            return await readFile(tempFilePath, "utf-8");
        } catch {
            return null;
        }
    }

    /**
     * Detects whether a patch's additions are already present in `content`.
     * Uses `git apply --reverse --check` — if the reverse patch applies cleanly,
     * the forward patch is effectively a no-op (its +lines are already there and
     * its -lines are already gone). Used to distinguish "patch already applied"
     * from "patch base mismatch" after a forward-apply fails.
     */
    private async isPatchAlreadyApplied(
        patchContent: string,
        filePath: string,
        content: string,
        tempGit: GitClient,
        tempDir: string
    ): Promise<boolean> {
        const fileDiff = this.extractFileDiff(patchContent, filePath);
        if (!fileDiff) return false;
        try {
            const tempFilePath = join(tempDir, filePath);
            await mkdir(dirname(tempFilePath), { recursive: true });
            await writeFile(tempFilePath, content);
            await tempGit.exec(["add", filePath]);
            await tempGit.exec(["commit", "-m", `check already-applied ${filePath}`, "--allow-empty"]);
            await tempGit.execWithInput(["apply", "--reverse", "--check", "--allow-empty"], fileDiff);
            return true;
        } catch {
            return false;
        }
    }

    private extractFileDiff(patchContent: string, filePath: string): string | null {
        const lines = patchContent.split("\n");
        const diffLines: string[] = [];
        let inTargetFile = false;

        for (const line of lines) {
            if (line.startsWith("diff --git")) {
                if (inTargetFile) {
                    // Hit the next file's diff, stop collecting
                    break;
                }
                if (isDiffLineForFile(line, filePath)) {
                    inTargetFile = true;
                    diffLines.push(line);
                }
                continue;
            }

            if (inTargetFile) {
                diffLines.push(line);
            }
        }

        return diffLines.length > 0 ? diffLines.join("\n") + "\n" : null;
    }

    private extractRenameSource(patchContent: string, targetFilePath: string): string | null {
        const lines = patchContent.split("\n");
        let inTargetFile = false;

        for (const line of lines) {
            if (line.startsWith("diff --git")) {
                if (inTargetFile) break; // past the target block
                inTargetFile = isDiffLineForFile(line, targetFilePath);
                continue;
            }
            if (!inTargetFile) continue;

            // Stop at first hunk — rename headers appear before @@
            if (line.startsWith("@@")) break;

            if (line.startsWith("rename from ")) {
                return line.slice("rename from ".length);
            }
        }

        return null;
    }

    private extractNewFileFromPatch(patchContent: string, filePath: string): string | null {
        const lines = patchContent.split("\n");
        const addedLines: string[] = [];
        let inTargetFile = false;
        let inHunk = false;
        let isNewFile = false;
        let noTrailingNewline = false;

        for (const line of lines) {
            if (line.startsWith("diff --git")) {
                if (inTargetFile) break; // hit next file's diff
                inTargetFile = isDiffLineForFile(line, filePath);
                inHunk = false;
                isNewFile = false;
                continue;
            }
            if (!inTargetFile) continue;

            if (!inHunk) {
                if (line === "--- /dev/null" || line.startsWith("new file mode")) {
                    isNewFile = true;
                }
            }

            if (line.startsWith("@@")) {
                if (!isNewFile) return null; // modification diff, not a new-file creation
                inHunk = true;
                continue;
            }
            if (!inHunk) continue;

            if (line === "\\ No newline at end of file") {
                noTrailingNewline = true;
                continue;
            }

            if (line.startsWith("+") && !line.startsWith("+++")) {
                addedLines.push(line.slice(1));
            }
        }

        if (addedLines.length === 0) return null;
        return addedLines.join("\n") + (noTrailingNewline ? "" : "\n");
    }
}

/**
 * Lines present in BOTH base and theirs (unchanged context in customer's
 * THEIRS) but absent from finalContent. Mirrors the slow-path detection in
 * ThreeWayMerge.computeDroppedContextLines so the warning surfaces
 * regardless of whether `git apply --3way` (fast path) or per-file diff3
 * (slow path) produced finalContent. Order preserved per first occurrence
 * in `base`. Multi-occurrence lines are reported as dropped when their
 * multiplicity decreased.
 */
function computeDroppedContextLinesForFile(
    base: string,
    theirs: string,
    finalContent: string
): string[] {
    const baseLines = base.split("\n");
    const theirsLines = theirs.split("\n");
    const finalLines = finalContent.split("\n");

    const baseCounts = new Map<string, number>();
    const theirsCounts = new Map<string, number>();
    const finalCounts = new Map<string, number>();

    for (const l of baseLines) baseCounts.set(l, (baseCounts.get(l) ?? 0) + 1);
    for (const l of theirsLines) theirsCounts.set(l, (theirsCounts.get(l) ?? 0) + 1);
    for (const l of finalLines) finalCounts.set(l, (finalCounts.get(l) ?? 0) + 1);

    const dropped: string[] = [];
    const seen = new Set<string>();
    for (const line of baseLines) {
        if (seen.has(line)) continue;
        const inBase = baseCounts.get(line) ?? 0;
        const inTheirs = theirsCounts.get(line) ?? 0;
        const inFinal = finalCounts.get(line) ?? 0;
        if (inBase > 0 && inTheirs > 0 && inFinal < Math.min(inBase, inTheirs)) {
            dropped.push(line);
            seen.add(line);
        }
    }
    return dropped;
}

/**
 * Check if a `diff --git` line targets the given file path.
 * Uses a regex anchored to the line format to avoid substring false positives
 * (e.g., `lib/foo.ts` matching `b/lib/foo.ts-backup`).
 */
function isDiffLineForFile(diffLine: string, filePath: string): boolean {
    const match = diffLine.match(/^diff --git a\/.+ b\/(.+)$/);
    return match !== null && match[1] === filePath;
}
